import {
  Table,
  Tag,
  Card,
  Popconfirm,
  Descriptions,
  theme,
  TableProps,
  Typography,
  Empty,
  Spin,
} from "antd";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { useEffect, useState, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import styles from "./NotificationsTable.module.css";
import {
  cancelScheduledPushThunk,
  getPushesScheduledThunk,
  selectPushesScheduled,
} from "../../notificationsSlice";
import dayjs from "dayjs";
import { IScheduledPush } from "../../../../interfaces/scheduledPush.interface";
import { useNavigate } from "react-router-dom";
import { TableParams } from "../../../../interfaces/tableParams.interface";
import TargetAttributeTagsPreview from "../Target-Attribute-Tags-Preview/TargetAttributeTagsPreview";

const { Text } = Typography;

interface NotificationsTableProps {
  className?: string;
}

const NotificationsTable: React.FC<NotificationsTableProps> = ({
  className,
}) => {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(false);
  const pushesScheduled = useAppSelector(selectPushesScheduled);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const tableData = useMemo(
    () =>
      pushesScheduled?.map((p) => ({
        ...p,
        key: p._id,
      })) || [],
    [pushesScheduled]
  );

  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: { current: 1, pageSize: 10 },
  });

  useEffect(() => {
    fetchPushesScheduled();
  }, []);

  const fetchPushesScheduled = async () => {
    setLoading(true);
    try {
      await dispatch(getPushesScheduledThunk());
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange: TableProps<IScheduledPush>["onChange"] = (
    pagination
  ) => {
    setTableParams({
      pagination,
    });
  };

  const columns: TableProps<IScheduledPush>["columns"] = [
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      className: styles.table_business_name_column,
      key: "timestamp",
      render: (_, record) => {
        const timestamp = dayjs(record.timestamp);
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: token.paddingXXS,
            }}
          >
            <Text>
              {timestamp.isValid()
                ? timestamp.format("DD/MM/YYYY")
                : "Invalid Date"}
            </Text>
            <Text type="secondary">
              {timestamp.isValid() ? timestamp.format("HH:mm") : ""}
            </Text>
          </div>
        );
      },
    },
    {
      title: "Type",
      key: "type",
      dataIndex: "type",
      className: styles.table_column,
      render: (_, record) => (
        <Tag color={token.colorPrimary} key={record._id + "type"}>
          {record.notification.type}
        </Tag>
      ),
    },
    {
      title: "Sent To",
      dataIndex: "targetAttributes",
      key: "targetAttributes",
      className: styles.table_column,
      render: (_, record) => (
        <TargetAttributeTagsPreview
          scheduledPush={record}
          key={record._id + "targetAttributes"}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: token.marginXXS,
          }}
        />
      ),
    },
  ];

  const expandedRowRender = (record: IScheduledPush) => (
    <Card
      size="small"
      bordered={false}
      style={{
        background: token.colorBgContainer,
        borderRadius: token.borderRadiusLG,
      }}
    >
      <Descriptions
        column={{ xs: 1, sm: 2 }}
        labelStyle={{
          opacity: 0.7,
          color: token.colorTextSecondary,
          minWidth: "130px",
        }}
        contentStyle={{
          color: token.colorText,
        }}
      >
        {record.notification?.type === "EMAIL" && (
          <>
            <Descriptions.Item label="Template">
              {record.notification.template}
            </Descriptions.Item>
            <Descriptions.Item label="Language">
              {record.notification.locale}
            </Descriptions.Item>
            {Object.entries(record.notification.data || {}).map(
              ([key, value]) => (
                <Descriptions.Item key={key} label={key}>
                  {value}
                </Descriptions.Item>
              )
            )}
          </>
        )}
        {record.notification?.type !== "EMAIL" && (
          <>
            {record.notification?.title && (
              <Descriptions.Item label="Title">
                {record.notification.title}
              </Descriptions.Item>
            )}
            {record.notification?.description && (
              <Descriptions.Item label="Description">
                {record.notification.description}
              </Descriptions.Item>
            )}
          </>
        )}
      </Descriptions>
    </Card>
  );

  return (
    <div className={`${styles.container} ${className || ""}`}>
      <Table
        dataSource={tableData}
        columns={columns}
        pagination={{
          ...tableParams.pagination,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} items`,
        }}
        loading={{
          spinning: loading,
          tip: "Loading notifications...",
        }}
        onChange={handleTableChange}
        expandable={{
          expandIcon: ({ expanded, onExpand, record }) =>
            expanded ? (
              <UpOutlined
                style={{
                  fontSize: token.fontSizeLG,
                  color: token.colorPrimary,
                  opacity: 0.8,
                }}
                onClick={(e) => onExpand(record, e)}
              />
            ) : (
              <DownOutlined
                style={{
                  fontSize: token.fontSizeLG,
                  color: token.colorTextSecondary,
                  opacity: 0.6,
                }}
                onClick={(e) => onExpand(record, e)}
              />
            ),
          expandedRowRender,
        }}
        locale={{
          emptyText: <Empty description="No notifications found" />,
        }}
      />
    </div>
  );
};

export default NotificationsTable;
